/**
 * Streaks / daily practice — Firestore-backed, Google account only.
 *
 * Guests / signed-out users do not see a live streak (no local fake progress).
 */

import { doc, getDoc, setDoc } from "firebase/firestore";
import { getDb, getFirebaseAuth } from "./firebase/config";

export interface StreakData {
  current: number;
  best: number;
  lastDate: string;
  weekDays: string[];
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function todayStr(): string {
  return isoDate(new Date());
}

function yesterdayStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return isoDate(d);
}

function dayLetter(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return ["S", "M", "T", "W", "T", "F", "S"][d.getDay()] ?? "·";
}

const DEFAULT_STREAK: StreakData = { current: 0, best: 0, lastDate: "", weekDays: [] };

/** Google (non-anonymous) uid, or null. */
export function streakAccountUid(): string | null {
  const user = getFirebaseAuth()?.currentUser;
  if (!user || user.isAnonymous) return null;
  return user.uid;
}

export async function loadStreak(): Promise<StreakData> {
  const uid = streakAccountUid();
  const db = getDb();
  if (!uid || !db) return { ...DEFAULT_STREAK };
  try {
    const snap = await getDoc(doc(db, "users", uid));
    return (snap.data()?.streaks as StreakData) ?? { ...DEFAULT_STREAK };
  } catch {
    return { ...DEFAULT_STREAK };
  }
}

export async function recordPractice(): Promise<StreakData> {
  const uid = streakAccountUid();
  const db = getDb();
  if (!uid || !db) return { ...DEFAULT_STREAK };

  const streak = await loadStreak();
  const today = todayStr();
  if (streak.lastDate === today) return streak;

  if (streak.lastDate === yesterdayStr()) streak.current += 1;
  else streak.current = 1;

  streak.best = Math.max(streak.best, streak.current);
  streak.lastDate = today;
  streak.weekDays = [...streak.weekDays.filter((d) => d !== today), today].slice(-7);

  try {
    await setDoc(doc(db, "users", uid), { streaks: streak }, { merge: true });
  } catch {
    /* offline */
  }
  return streak;
}

/** Shown on hub when Firebase works but user is not on a Google account. */
export function renderStreakSignInGate(): string {
  return `
    <div class="streak-plaque streak-plaque--gate" role="status">
      <div class="streak-plaque-head">
        <span class="streak-plaque-kicker">DAILY STREAK</span>
      </div>
      <p class="streak-cta">Sign in with Google to track your daily streak across devices.</p>
    </div>
  `;
}

/** Game-HUD streak plaque — week runs left→right (oldest → today). */
export function renderStreakBar(streak: StreakData): string {
  const today = todayStr();
  const days: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(isoDate(d));
  }
  const todayDone = streak.lastDate === today;
  const cells = days
    .map((d, idx) => {
      const done = streak.weekDays.includes(d);
      const isToday = d === today;
      return `<span class="streak-day${done ? " is-done" : ""}${isToday ? " is-today" : ""}" title="${d}" style="order:${idx}"><i>${dayLetter(d)}</i></span>`;
    })
    .join("");

  return `
    <div class="streak-plaque" role="status" aria-label="${streak.current} day streak">
      <div class="streak-plaque-head">
        <span class="streak-plaque-kicker">DAILY STREAK</span>
        <span class="streak-plaque-best">BEST ${streak.best}</span>
      </div>
      <div class="streak-plaque-row">
        <div class="streak-flame" aria-hidden="true">${streak.current >= 3 ? "🔥" : "⚡"}</div>
        <div class="streak-num-wrap">
          <strong class="streak-num">${streak.current}</strong>
          <span class="streak-num-label">DAYS</span>
        </div>
        <div class="streak-week" dir="ltr">${cells}</div>
      </div>
      <p class="streak-cta">${todayDone ? "Today’s mission logged — keep the flame." : "Clear a class or Vault piece to keep the flame."}</p>
    </div>
  `;
}
